import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Cliente, Concepto } from '../types';
import contractsService from '../services/contracts.service';
import clientsService from '../services/clients.service';
import conceptsService from '../services/concepts.service';
import { useAuth } from '../contexts/AuthContext';

interface ContratoFormData {
  numeroContrato: string;
  objeto: string;
  clienteId: number;
  comercialId: number;
  valorTotal: number;
  tipoServicio: string;
  fechaInicio: string;
  fechaFin: string;
  estado: string;
  observaciones: string;
}

interface DetalleContrato {
  id?: number;
  conceptoId: number;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  descuentoPct: number;
  subtotalBruto: number;
  montoDescuento: number;
  subtotalNeto: number;
}

const ContratoForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clienteId = searchParams.get('clienteId');
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [detalles, setDetalles] = useState<DetalleContrato[]>([]);
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [formData, setFormData] = useState<ContratoFormData>({
    numeroContrato: '',
    objeto: '',
    clienteId: clienteId ? parseInt(clienteId) : 0,
    comercialId: user?.id || 0, // ID del usuario logueado
    valorTotal: 0,
    tipoServicio: 'consultoria',
    fechaInicio: '',
    fechaFin: '',
    estado: 'vigente',
    observaciones: '',
  });

  // Actualizar comercialId cuando el usuario esté disponible
  useEffect(() => {
    if (user && !isEditing) {
      setFormData(prev => ({
        ...prev,
        comercialId: user.id
      }));
    }
  }, [user, isEditing]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientesData = await clientsService.getClientes(1, 1000);
        const clientesList = clientesData.clientes || [];
        setClientes(clientesList);
        setFilteredClientes(clientesList);

        const conceptosData = await conceptsService.getConceptosActivos();
        setConceptos(conceptosData || []);

        if (isEditing && id) {
          const contrato = await contractsService.getContratoById(parseInt(id));
          setFormData({
            numeroContrato: contrato.numeroContrato,
            objeto: contrato.objeto,
            clienteId: contrato.clienteId,
            comercialId: contrato.comercialId || user?.id || 0,
            valorTotal: contrato.valorTotal,
            tipoServicio: contrato.tipoServicio,
            fechaInicio: contrato.fechaInicio.split('T')[0],
            fechaFin: contrato.fechaFin.split('T')[0],
            estado: contrato.estado,
            observaciones: contrato.observaciones || '',
          });
          
          // Cargar detalles existentes
          if (contrato.detalles && contrato.detalles.length > 0) {
            const detallesFormateados = contrato.detalles.map((d: any) => ({
              id: d.id,
              conceptoId: d.conceptoId,
              descripcion: d.descripcion,
              cantidad: d.cantidad,
              precioUnitario: Number(d.precioUnitario),
              descuentoPct: Number(d.descuentoPct) || 0,
              subtotalBruto: Number(d.subtotalBruto),
              montoDescuento: Number(d.montoDescuento),
              subtotalNeto: Number(d.subtotalNeto),
            }));
            setDetalles(detallesFormateados);
          }
          
          // Mostrar el cliente seleccionado en el campo de búsqueda
          const selectedCliente = clientesList.find(c => c.id === contrato.clienteId);
          if (selectedCliente) {
            setClienteSearch(`${selectedCliente.nombre} - ${selectedCliente.nit}`);
          }
        } else if (clienteId) {
          // Si se pasa clienteId por parámetro, mostrar el cliente seleccionado
          const selectedCliente = clientesList.find(c => c.id === parseInt(clienteId));
          if (selectedCliente) {
            setClienteSearch(`${selectedCliente.nombre} - ${selectedCliente.nit}`);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
      }
    };

    fetchData();
  }, [id, isEditing, user, clienteId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.cliente-dropdown')) {
        setShowClienteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'clienteId' || name === 'comercialId' ? parseInt(value) : 
               name === 'valorTotal' ? parseFloat(value) || 0 : value
    }));
  };

  const handleClienteSearch = (value: string) => {
    setClienteSearch(value);
    setShowClienteDropdown(true);
    
    if (value.trim() === '') {
      setFilteredClientes(clientes);
    } else {
      const filtered = clientes.filter(cliente => 
        `${cliente.nombre} - ${cliente.nit}`.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClientes(filtered);
    }
  };

  const handleClienteSelect = (cliente: Cliente) => {
    setFormData(prev => ({ ...prev, clienteId: cliente.id }));
    setClienteSearch(`${cliente.nombre} - ${cliente.nit}`);
    setShowClienteDropdown(false);
  };

  // Funciones para manejar detalles
  const agregarDetalle = () => {
    const nuevoDetalle: DetalleContrato = {
      conceptoId: 0,
      descripcion: '',
      cantidad: 1,
      precioUnitario: 0,
      descuentoPct: 0,
      subtotalBruto: 0,
      montoDescuento: 0,
      subtotalNeto: 0,
    };
    setDetalles([...detalles, nuevoDetalle]);
  };

  const handleConceptoChange = (index: number, conceptoId: string) => {
    const concepto = conceptos.find((c: Concepto) => c.id === parseInt(conceptoId));
    if (concepto) {
      const nuevosDetalles = [...detalles];
      const detalle = nuevosDetalles[index];
      
      detalle.conceptoId = concepto.id;
      detalle.descripcion = concepto.nombre;
      detalle.precioUnitario = concepto.precioLista;
      
      // Recalcular totales
      const cantidad = detalle.cantidad;
      const precioUnitario = concepto.precioLista;
      const descuentoPct = detalle.descuentoPct || 0;
      
      detalle.subtotalBruto = cantidad * precioUnitario;
      detalle.montoDescuento = (detalle.subtotalBruto * descuentoPct) / 100;
      detalle.subtotalNeto = detalle.subtotalBruto - detalle.montoDescuento;
      
      setDetalles(nuevosDetalles);
    }
  };

  const eliminarDetalle = (index: number) => {
    const nuevosDetalles = detalles.filter((_, i) => i !== index);
    setDetalles(nuevosDetalles);
  };

  const actualizarDetalle = (index: number, campo: keyof DetalleContrato, valor: any) => {
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index] = { ...nuevosDetalles[index], [campo]: valor };

    // Recalcular totales del detalle
    const detalle = nuevosDetalles[index];
    const cantidad = Number(detalle.cantidad) || 0;
    const precioUnitario = Number(detalle.precioUnitario) || 0;
    const descuentoPct = Number(detalle.descuentoPct) || 0;

    detalle.subtotalBruto = cantidad * precioUnitario;
    detalle.montoDescuento = (detalle.subtotalBruto * descuentoPct) / 100;
    detalle.subtotalNeto = detalle.subtotalBruto - detalle.montoDescuento;

    setDetalles(nuevosDetalles);
  };

  // Calcular totales generales
  const calcularTotales = () => {
    const totalBruto = detalles.reduce((sum, d) => sum + (Number(d.subtotalBruto) || 0), 0);
    const totalDescuento = detalles.reduce((sum, d) => sum + (Number(d.montoDescuento) || 0), 0);
    const totalNeto = detalles.reduce((sum, d) => sum + (Number(d.subtotalNeto) || 0), 0);
    return { totalBruto, totalDescuento, totalNeto };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing && id) {
        const totales = calcularTotales();
        const submitData = {
          ...formData,
          valorTotal: totales.totalNeto,
          totalBruto: totales.totalBruto,
          totalDescuento: totales.totalDescuento,
          totalNeto: totales.totalNeto,
          fechaInicio: formData.fechaInicio,
          fechaFin: formData.fechaFin,
          estado: formData.estado as 'vigente' | 'no_vigente',
        };
        await contractsService.updateContrato(parseInt(id), submitData);
        
        // Obtener detalles existentes
        const contratoActual = await contractsService.getContratoById(parseInt(id));
        const detallesExistentes = contratoActual.detalles || [];
        
        // Eliminar detalles existentes
        for (const detalleExistente of detallesExistentes) {
          await contractsService.deleteDetalleContrato(detalleExistente.id);
        }
        
        // Crear nuevos detalles
        if (detalles.length > 0) {
          for (const detalle of detalles) {
            await contractsService.createDetalleContrato(parseInt(id), {
              conceptoId: detalle.conceptoId,
              descripcion: detalle.descripcion,
              cantidad: detalle.cantidad,
              precioUnitario: detalle.precioUnitario,
              descuentoPct: detalle.descuentoPct || 0,
            });
          }
        }
        
        alert('Contrato actualizado exitosamente');
      } else {
        // Para crear, no enviar el campo estado ni los totales calculados (se calculan en backend)
        const { estado, ...createData } = formData;
        const totales = calcularTotales();
        const submitData = {
          ...createData,
          valorTotal: totales.totalNeto,
          fechaInicio: formData.fechaInicio,
          fechaFin: formData.fechaFin,
        };
        const contratoCreado = await contractsService.createContrato(submitData);
        
        // Guardar detalles si existen
        if (detalles.length > 0) {
          for (const detalle of detalles) {
            await contractsService.createDetalleContrato(contratoCreado.id, {
              conceptoId: detalle.conceptoId,
              descripcion: detalle.descripcion,
              cantidad: detalle.cantidad,
              precioUnitario: detalle.precioUnitario,
              descuentoPct: detalle.descuentoPct || 0,
            });
          }
        }
        
        alert('Contrato creado exitosamente');
        navigate(`/contratos/${contratoCreado.id}/editar`);
      }
    } catch (error) {
      console.error('Error saving contrato:', error);
      alert('Error al guardar el contrato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/contratos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a contratos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Contrato' : 'Nuevo Contrato'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Actualiza la información del contrato' : 'Registra un nuevo contrato en el sistema'}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información Básica */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información Básica
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="numeroContrato" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Contrato *
                </label>
                <input
                  type="text"
                  id="numeroContrato"
                  name="numeroContrato"
                  required
                  value={formData.numeroContrato}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ej: CONT-2024-001"
                />
              </div>
              
              <div className="relative cliente-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={clienteSearch}
                    onChange={(e) => handleClienteSearch(e.target.value)}
                    onFocus={() => setShowClienteDropdown(true)}
                    placeholder="Buscar cliente..."
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    autoComplete="off"
                  />
                  {showClienteDropdown && filteredClientes.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                      {filteredClientes.map((cliente) => (
                        <div
                          key={cliente.id}
                          onClick={() => handleClienteSelect(cliente)}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                        >
                          {cliente.nombre} - {cliente.nit}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {clienteSearch && !clientes.find(c => `${c.nombre} - ${c.nit}` === clienteSearch) && (
                  <p className="text-red-500 text-xs mt-1">Selecciona un cliente válido de la lista</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="objeto" className="block text-sm font-medium text-gray-700 mb-2">
                  Objeto del Contrato *
                </label>
                <input
                  type="text"
                  id="objeto"
                  name="objeto"
                  required
                  value={formData.objeto}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Descripción del objeto del contrato"
                />
              </div>

              <div>
                <label htmlFor="valorTotal" className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Total (calculado desde items)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="valorTotal"
                    name="valorTotal"
                    value={formatCurrency(calcularTotales().totalNeto)}
                    readOnly
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Este valor se calcula automáticamente sumando los items del contrato
                </p>
              </div>

              <div>
                <label htmlFor="tipoServicio" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Servicio *
                </label>
                <select
                  id="tipoServicio"
                  name="tipoServicio"
                  required
                  value={formData.tipoServicio}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="puntoedu">PuntoEdu</option>
                  <option value="diplomas">Diplomas</option>
                  <option value="facturacion">Facturación</option>
                  <option value="carnets">Carnets</option>
                  <option value="papeleria">Papeleria</option>
                  <option value="app">Aplicacion</option>
                  <option value="otros">Otros</option>
                </select>
              </div>

              <div>
                <label htmlFor="comercial" className="block text-sm font-medium text-gray-700 mb-2">
                  Comercial Asignado
                </label>
                <input
                  type="text"
                  id="comercial"
                  readOnly
                  value={user ? `${user.nombre} (${user.email})` : 'Cargando...'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label htmlFor="fechaInicio" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Inicio *
                </label>
                <input
                  type="date"
                  id="fechaInicio"
                  name="fechaInicio"
                  required
                  value={formData.fechaInicio}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="fechaFin" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Fin *
                </label>
                <input
                  type="date"
                  id="fechaFin"
                  name="fechaFin"
                  required
                  value={formData.fechaFin}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-2">
                  Estado *
                </label>
                <select
                  id="estado"
                  name="estado"
                  required
                  value={formData.estado}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="vigente">Vigente</option>
                  <option value="no_vigente">No Vigente</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="observaciones"
                name="observaciones"
                rows={4}
                value={formData.observaciones}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Observaciones adicionales del contrato"
              />
            </div>
          </div>

          {/* Detalles del Contrato */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">Detalles / Servicios</h2>
              <button
                type="button"
                onClick={agregarDetalle}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Agregar Detalle
              </button>
            </div>

            {detalles.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No hay detalles agregados. Click en "Agregar Detalle" para comenzar.
              </div>
            ) : (
              <div className="space-y-4">
                {detalles.map((detalle, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Primera fila: Concepto */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Concepto / Servicio *
                        </label>
                        <select
                          value={detalle.conceptoId || ''}
                          onChange={(e) => handleConceptoChange(index, e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Seleccionar concepto...</option>
                          {conceptos.map((concepto) => (
                            <option key={concepto.id} value={concepto.id}>
                              {concepto.codigo ? `${concepto.codigo} - ` : ''}{concepto.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Segunda fila: Descripción */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Descripción adicional (opcional)
                        </label>
                        <textarea
                          value={detalle.descripcion}
                          onChange={(e) => actualizarDetalle(index, 'descripcion', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Descripción o notas adicionales del servicio"
                        />
                      </div>

                      {/* Tercera fila: Cantidad, Precio, Descuento, Eliminar */}
                      <div className="grid grid-cols-12 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Cantidad *
                        </label>
                        <input
                          type="number"
                          value={detalle.cantidad}
                          onChange={(e) => actualizarDetalle(index, 'cantidad', Number(e.target.value))}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Precio Unit.
                        </label>
                        <input
                          type="text"
                          value={detalle.precioUnitario.toLocaleString('es-CO')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            actualizarDetalle(index, 'precioUnitario', Number(value) || 0);
                          }}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Desc. %
                        </label>
                        <input
                          type="number"
                          value={detalle.descuentoPct}
                          onChange={(e) => actualizarDetalle(index, 'descuentoPct', Number(e.target.value))}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={() => eliminarDetalle(index)}
                          className="w-full px-3 py-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md"
                        >
                          <TrashIcon className="h-5 w-5 mx-auto" />
                        </button>
                      </div>
                      </div>

                      {/* Subtotal */}
                      <div className="text-right text-sm text-gray-600">
                        Subtotal: <span className="font-semibold">{formatCurrency(detalle.subtotalNeto)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Totales */}
                <div className="border-t-2 border-gray-300 pt-4 mt-4">
                  <div className="flex justify-end space-x-8 text-sm">
                    <div>
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="ml-2 font-semibold">{formatCurrency(calcularTotales().totalBruto)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Descuento:</span>
                      <span className="ml-2 font-semibold text-red-600">-{formatCurrency(calcularTotales().totalDescuento)}</span>
                    </div>
                    <div>
                      <span className="text-gray-700 font-medium">Total:</span>
                      <span className="ml-2 font-bold text-lg text-blue-600">{formatCurrency(calcularTotales().totalNeto)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/contratos')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Contrato')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContratoForm;